import { useEffect, useState } from 'react';
import { getCoreRowModel, useReactTable, VisibilityState } from '@tanstack/react-table';
import { Search } from 'lucide-react';

import { MultiSelect } from '@/components/atoms/multi-select';
import { Select } from '@/components/atoms/select';
import { ColumnVisibilityPicker, DataTable } from '@/components/data-table';
import DateRange from '@/components/daterange';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRouter } from '@/lib/router';
import { cn } from '@/lib/utils';

import { useColumnsCtx } from './call-columns-hook';
import { BOOKING_STATUSES_OPTIONS, Filters, SortScore, useCallsTableCtx } from './call-table-provider';
import { CALL_COLUMNS } from './columns';

export type Duration = 'all' | 'lt_30' | '30_120' | 'gt_120';
export type Sort = 'newest' | 'oldest';

const isFilterActive = (filters: Filters, filter: keyof Filters) => {
  const value = filters[filter];
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return value !== null;
  return Boolean(value);
};

const isAnyFilterActive = (filters: Filters) => {
  return Object.keys(filters).some((key) => isFilterActive(filters, key as keyof Filters));
};

const countActiveFilters = (filters: Filters) => {
  return Object.keys(filters).filter((key) => isFilterActive(filters, key as keyof Filters)).length;
};

const DURATION_OPTIONS: { label: string; value: Duration }[] = [
  { label: 'All durations', value: 'all' },
  { label: '< 30s', value: 'lt_30' },
  { label: '30 - 120s', value: '30_120' },
  { label: '> 120s', value: 'gt_120' },
];

const SORT_OPTIONS: { label: string; value: Sort }[] = [
  { label: 'Newest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
];

const SORT_SCORE_OPTIONS: { label: string; value: SortScore }[] = [
  { label: 'No sorting', value: 'none' },
  { label: 'Better first', value: 'better_first' },
  { label: 'Better last', value: 'better_last' },
];

interface CallTableParams {
  csr?: string | null;
}

export function CallTable({ csr }: CallTableParams) {
  const {
    searchState: { filters, sortBy, sortScore },
    setFilters,
    setSortBy,
    setSortScore,
    isLoading,
    calls,
  } = useCallsTableCtx();
  const router = useRouter();
  const { columns, visibility } = useColumnsCtx();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => visibility);

  useEffect(() => {
    setColumnVisibility(visibility);
  }, [visibility]);

  const table = useReactTable({
    data: calls,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
  });

  const onClearFilters = () => {
    if (!isAnyFilterActive(filters)) {
      return;
    }
    setFilters({
      dateRange: null,
      csr: [],
      team: [],
      search: undefined,
    });
  };
  return (
    <div className='space-y-3'>
      {/* Search bar section */}
      <div className='mb-4 flex items-center justify-between gap-2'>
        <div className='relative max-w-sm flex-1'>
          <Search className='absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search phone numbers, CSR names, Teams...'
            value={filters.search ?? ''}
            onChange={(e) => setFilters({ search: e.target.value })}
            className='pl-10'
          />
        </div>
        <div className='flex items-center gap-2'>
          {isAnyFilterActive(filters) && <Badge variant='secondary'>{countActiveFilters(filters)} filters</Badge>}
          <div>
            <Button variant='secondary' size='sm' disabled={!isAnyFilterActive(filters)} onClick={onClearFilters}>
              Clear filters
            </Button>
          </div>
        </div>
      </div>

      {/* Filters section */}
      <div className='mb-4 flex items-center justify-between gap-2'>
        <div className='flex w-full items-center gap-2'>
          {/* Date range (simple two-date inputs) */}
          <div className='flex items-center gap-1'>
            <DateRange
              size='sm'
              classNameButton='w-52'
              dateFormat='LLL. d'
              onCommit={(range) => {
                setFilters({ dateRange: range ?? null });
              }}
              value={filters.dateRange ?? undefined}
            />
          </div>

          <Select
            name={DURATION_OPTIONS[0].label}
            options={DURATION_OPTIONS}
            value={filters.duration ?? DURATION_OPTIONS[0].value}
            onChange={(values) => setFilters({ duration: values })}
          />

          <DistinctFilter fieldName='user_name' filterKey='csr' label='Select CSR' />
          <DistinctFilter fieldName='team' filterKey='team' label='Select Team' />
          <BookingStatusFilter />

          <div>
            <Select
              name={'Sort by'}
              options={SORT_OPTIONS}
              value={sortBy ?? SORT_OPTIONS[0].value}
              onChange={(values) => setSortBy(values)}
            />
          </div>

          <div>
            <Select
              name={'Call score'}
              options={SORT_SCORE_OPTIONS}
              value={sortScore ?? SORT_SCORE_OPTIONS[0].value}
              onChange={(values) => setSortScore(values)}
            />
          </div>
        </div>
        <ColumnVisibilityPicker table={table} />
      </div>

      <div className='w-full overflow-x-auto'>
        <div className={cn('min-w-[900px] rounded-lg border', isLoading && 'animate-pulse')}>
          <DataTable
            table={table}
            columns={CALL_COLUMNS}
            rowClassName='cursor-pointer'
            onRowClick={(row) => {
              const callId = row.original.id;
              router.push(`/analytics/call-analytics/${callId}`);
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface DistinctMultiFilterProps {
  fieldName: 'user_name' | 'team'; // API distinct field name
  filterKey: 'csr' | 'team';
  label: string;
}

function DistinctFilter({ fieldName, filterKey, label }: DistinctMultiFilterProps) {
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const {
    searchState: { filters },
    setFilters,
  } = useCallsTableCtx();

  const { data: values } = api.externalCalls.getDistinctValuesExternalCallsDistinctValuesFieldNameGet.useQuery({
    path: { field_name: fieldName },
    query: { office_id: officeId },
  });

  const selected = (filters[filterKey] ?? []) as string[];
  const onChange = (values: string[]) => {
    console.log('onChange values:', values);
    setFilters({ [filterKey]: values });
  };

  return (
    <MultiSelect
      options={values?.map((v) => ({ label: v.value, value: v.key })) ?? []}
      selected={selected}
      label={label}
      onChange={onChange}
    />
  );
}

function BookingStatusFilter() {
  const {
    searchState: { filters },
    setFilters,
  } = useCallsTableCtx();

  return (
    <Select
      name={BOOKING_STATUSES_OPTIONS[0].label}
      options={[...BOOKING_STATUSES_OPTIONS]}
      value={filters.booking_status ?? BOOKING_STATUSES_OPTIONS[0].value}
      onChange={(values) => setFilters({ booking_status: values })}
    />
  );
}
